import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Divider, Chip } from "@mui/material";
import { CustomButton } from "../../../v2/MUI_custom_component";
import BenefitCard from "../../../v2/Benefits/Beneficiary/Components/BenefitCard";
import FirmDetails from "../../../v2/Benefits/Beneficiary/Components/FirmDetails";
import { Edit, EditOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { getShopInsuranceDetailAction } from "./APIs/action";
import { toast } from "react-toastify";

const getBooleanValue = (value) => {
    const isTruthy = value === true || value === "True" || value === "true" || value === "yes";
    const isFalsy = value === false || value === "False" || value === "false" || value === "no" || value === null;
    
    if (isTruthy) {
        return { display: "Yes", normalized: "true" };
    }
    if (isFalsy) {
        return { display: "No", normalized: "false" };
    }
    
    // For non-boolean values, return as-is
    return { display: value, normalized: String(value).toLowerCase() };
};

// Helper function to check field dependencies
const shouldShowField = (field, allFields) => {
    if (!field.dependsOn) return true;
    
    const [depId, depVal] = field.dependsOn.split(":");
    const dependentField = allFields.find(f => f.id === depId);
    if (!dependentField) {
        console.warn(`Dependent field ${depId} not found for field ${field.id}`);
        return false;
    }
    
    const fieldValue = getBooleanValue(dependentField.value).normalized;
    const expectedValue = depVal.toLowerCase();
    
    return fieldValue === expectedValue;
};


const ShopInsuranceDetails = (data) => {
	const navigate = useNavigate()
	const location = useLocation();
	const [isEditing, setIsEditing] = useState(false);
	const [loader, setLoader] = useState(false);
	const insuranceType = location?.state?.insurance_type;
	const selectedCompanyEntity = location?.state?.selectedCompanyEntity;
	const [shopDetails, setShopDetails] = useState([])
	const { benefit_card, selected_firm, shop_details } = shopDetails;
	
	const handleEdit = (shop_details) => {
		setIsEditing(true);
		navigate('/beneficiary/add-shop-details', {
			state: {
				shop_data: shop_details,
				isEdit: true,
				selectedCompanyEntity: selectedCompanyEntity,
			},
		});
	};
	const getShopInsuranceDetailInfo = async (selectedCompanyEntity) => {
		setLoader(true);
		const { data, error, message } = await getShopInsuranceDetailAction(selectedCompanyEntity);
		setLoader(false);
		if (!error) {
			setShopDetails(data);
		} else {
			toast.error(message);
		}
	};
	useEffect(() => {
		if (insuranceType && selectedCompanyEntity) {
			getShopInsuranceDetailInfo(selectedCompanyEntity);
		} else {
			navigate('/beneficiary');
		}
	}, [insuranceType, selectedCompanyEntity]);

	if (loader) {
		return (
			<Box sx={{ p: { xs: 1.5, md: 3 } }}>
				<Typography variant="body2" color="text.secondary">
					Loading...
				</Typography>
			</Box>
		);
	} 
	return (
		<Box sx={{ p: { xs: 1.5, md: 3 } }}>
			<Grid container spacing={2} sx={{ alignItems: 'stretch', mb: 3 }}>
				<Grid item xs={12} sm={6}>
				<BenefitCard item={benefit_card} sx={{ height: '100%' }} />
				</Grid>
				<Grid item xs={12} sm={6}>
				<Box
					sx={{
						background: '#fff',
						p: 2,
						borderRadius: 3,
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-evenly',
					}}
				>
					{(selected_firm) && selected_firm.length > 0 ? (
					selected_firm.map((detail, index) => (
						<FirmDetails key={detail?.id || index} detail={detail} />
					))
					) : (
					<Typography variant="body2" color="text.secondary">
						No firm details available
					</Typography>
					)}
				</Box>
				</Grid>
			</Grid>
			<Grid container spacing={4}>
				<Grid item xs={12}>
				<Box sx={{ background: '#fff', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
					<Typography variant="h2">
						{shop_details?.header_title}
					</Typography>
					{shop_details?.is_editable && (
						<CustomButton
							startIcon={<EditOutlined />}
							variant="text"
							color="primary"
							onClick={() => handleEdit(shop_details?.questions)}
							sx={{
								'& .MuiButton-startIcon': { margin: 0 }
							}}
						/>
					)}
					</Box>
					<Box>
					{shop_details?.questions.map((section) => {
						return (
						<Box key={section.section} sx={{ display: 'flex', flexDirection: 'column',gap: 2, mb: 4}}>
							<Typography  variant="labelD" sx={{ mb: 1 }}>
							{section?.section}
							</Typography>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
								{section.fields.map((field) => {
									const allFields = shop_details?.questions?.flatMap(s => s.fields) || [];
										if (!shouldShowField(field, allFields)) {
										return null;
									}
									
									return (
										<Box key={field.id} sx={{ display: (field.type !== 'boolean' && field.type !== 'textfield') ? 'block' : 'flex' , justifyContent: 'space-between', gap: 2 }}>
											
											<Typography variant="body1" sx={{ 
												color: (field.type === 'textfield') ? '#666666' : 'inherit', 
												fontWeight: (field.type === 'textfield') ? 600 : 'inherit',
												display: (field.type === 'multi-select-chip' && (!field.value || field.value.length === 0)) ? 'none' : 
														field.type === 'multi-select-chip' ? 'none' : 'block' 
											}}>
												{field?.label}
											</Typography>
											{field.type === 'multi-select-chip' ? (
												field.value && field.value.length > 0 ? (
													<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1}}>
													{field?.value.map((val, i) => (
														<Box key={i} sx={{ px: 1.5, py: 0.5,  borderRadius: 1, bgcolor: 'grey.100', fontWeight: 600 }}>
															{val}
														</Box>
													))}
													</Box>
												) : (
													<Typography variant="body1" sx={{ fontWeight: 600 }}>
														Not Available
													</Typography>
												)
											) : (
												<Typography variant="body1" sx={{ 
													fontWeight: 600,
													textAlign: 'right',
													wordBreak: field?.value && String(field.value).length > 20 ? 'break-all' : 'normal',
													overflowWrap: 'break-word'
												}}>
													{field?.type === 'boolean'
														? getBooleanValue(field?.value).display
														: field?.id === 'q7' && field?.value
														? `₹ ${field?.value} L`
														: field?.value || 'Not Available'}
												</Typography>
											)}
										</Box>
									);
								})}
							</Box>
						</Box>
						);
					})}
					</Box>
				</Box>
				</Grid>
			</Grid>
		</Box>
	);
};

export default ShopInsuranceDetails;
